import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github-dark.css";
import type { ChatMessage, ProgressEntry } from "../hooks/useChatStream";

type Props = { message: ChatMessage };

const NODE_GLYPH: Record<ProgressEntry["kind"], string> = {
  tool_use: "",
  tool_ok: "✓",
  tool_error: "✗",
  thinking: "",
};

function countTools(entries: ProgressEntry[]): number {
  return entries.filter((e) => e.kind === "tool_use").length;
}

function summarize(entries: ProgressEntry[]): string {
  const tools = countTools(entries);
  if (tools === 0) return "Done";
  return `${tools} tool${tools === 1 ? "" : "s"}`;
}

/**
 * Forge progress trail — a connected timeline. While streaming it shows a
 * spinner and stays open; once the response settles it collapses to a one-line
 * summary ("5 tools") and the user can re-open it.
 */
function ProgressTrail({ entries, streaming }: { entries: ProgressEntry[]; streaming: boolean }) {
  const [open, setOpen] = useState(streaming);

  // Auto-open while streaming, auto-collapse when it finishes.
  useEffect(() => {
    setOpen(streaming);
  }, [streaming]);

  if (entries.length === 0 && !streaming) return null;

  const toolCount = countTools(entries);

  return (
    <div className={`progress-trail${open ? " open" : ""}`}>
      <button
        type="button"
        className="progress-trail-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {streaming ? (
          <span className="progress-trail-spin" aria-hidden="true" />
        ) : (
          <span className="progress-trail-done" aria-hidden="true" />
        )}
        <span className="progress-trail-summary">
          {streaming ? "Working…" : summarize(entries)}
        </span>
        <span className="progress-trail-meta">
          {!streaming && toolCount > 0 && <span>{toolCount} tools</span>}
          <span className="progress-trail-chev" aria-hidden="true">›</span>
        </span>
      </button>
      <div className="progress-trail-body">
        {entries.map((e) => (
          <div key={e.id} className={`progress-item progress-${e.kind}`}>
            <span className="progress-rail">
              <span className="progress-node">{NODE_GLYPH[e.kind]}</span>
            </span>
            <span className="progress-label">{e.label}</span>
          </div>
        ))}
        {streaming && (
          <div className="progress-item progress-pulse">
            <span className="progress-rail">
              <span className="progress-node" />
            </span>
            <span className="progress-label">working…</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Message({ message }: Props) {
  const { role, content, streaming, error, progress, interrupted } = message;
  const showEmpty = !content && !streaming && !interrupted;
  return (
    <div className={`message ${role}${error ? " error" : ""}${interrupted ? " interrupted" : ""}`}>
      <div className="message-content">
        <div className="message-role">{role === "assistant" ? "Ptero-Cloud AI" : role}</div>
        {role === "assistant" ? (
          <div className="markdown-body">
            {(progress && progress.length > 0) || streaming ? (
              <ProgressTrail entries={progress ?? []} streaming={streaming} />
            ) : null}
            {content && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            )}
            {showEmpty && <span className="message-empty">(empty)</span>}
            {streaming && content && <span className="streaming-caret" aria-hidden="true" />}
            {interrupted && <div className="message-interrupted">(stopped)</div>}
          </div>
        ) : (
          <>
            <pre className="user-text">{content}</pre>
            {showEmpty && <span className="message-empty">(empty)</span>}
            {streaming && content && <span className="streaming-caret" aria-hidden="true" />}
          </>
        )}
        {error && <div className="message-error">{error}</div>}
      </div>
    </div>
  );
}
